import { localToday } from './dateUtils';
/**
 * geminiService.js — Shared Gemini API client for Voice Dump + AI Coach.
 *
 * Dual-mode architecture:
 *   1. PROXY MODE (default) — Calls go through Cloud Functions proxy.
 *      Credits are deducted server-side. Gemini API key is in Secret Manager.
 *   2. BYOK MODE (fallback) — When credits are depleted and user has their
 *      own API key, calls go directly from browser to Gemini.
 *
 * Mode detection is handled by useAICreditStore.getAIMode().
 * This module exports the same API surface regardless of mode.
 */

import { GoogleGenAI } from '@google/genai';
import { SYSTEM_PROMPT, COACH_SYSTEM_PROMPT } from '../ai/prompts';
import { storage } from './storage';
import { proxyChatWithCoach, proxyVoiceDump } from './aiProxy';
import { getPlainText } from '../components/ui/DualModeEditor';
import useUserStore from '../stores/useUserStore';
import useConfigStore from '../stores/useConfigStore';

// ---------- BYOK model (single constant, runtime-configurable) ----------

/** The legacy literal — used whenever runtime config is absent or malformed. */
export const DEFAULT_BYOK_MODEL = 'gemini-3.5-flash';

/**
 * The Gemini model id for ALL direct BYOK calls. Reads
 * publicConfig.ai.clientDefaults.byokModel from the live config/public
 * projection; any absence/error falls back to DEFAULT_BYOK_MODEL so default
 * behavior never changes.
 */
export function getByokModel() {
  try {
    const m = useConfigStore.getState().publicConfig?.ai?.clientDefaults?.byokModel;
    return typeof m === 'string' && m ? m : DEFAULT_BYOK_MODEL;
  } catch {
    return DEFAULT_BYOK_MODEL;
  }
}

// ---------- AI Preference Access ----------
//
// SHARED CONTRACT keys live in useUserStore DEFAULT_PREFERENCES. We read them
// through getPreference (which falls back to DEFAULT_PREFERENCES), but ALSO
// carry local literal fallbacks so that if a key is somehow absent the AI still
// behaves EXACTLY as before. S9: aiTonePreset/aiModelTier are KEYS only — never
// raw prompts — and are forwarded to the server which owns the directive/model.

/** Default confidence gate used before the aiConfidenceThreshold pref existed. */
const DEFAULT_CONFIDENCE_THRESHOLD = 0.6;

function getPref(key, fallback) {
  try {
    const val = useUserStore.getState().getPreference(key);
    return val === undefined || val === null ? fallback : val;
  } catch {
    // Store not ready (e.g. isolated unit test) → preserve legacy behavior.
    return fallback;
  }
}

/**
 * Read the user's AI tone + model tier PREFERENCE KEYS for forwarding to the
 * server proxy. Returns only defined keys so callers can spread them and old
 * behavior is preserved when a pref is absent.
 * @returns {{ tonePreset?: string, modelTier?: string }}
 */
function getAIRoutingPrefs() {
  const out = {};
  const tone = getPref('aiTonePreset', undefined);
  const tier = getPref('aiModelTier', undefined);
  if (tone !== undefined) out.tonePreset = tone;
  if (tier !== undefined) out.modelTier = tier;
  return out;
}

/** The confidence gate for inbox auto-classification (pref-driven, 0..1). */
function getConfidenceThreshold() {
  const val = getPref('aiConfidenceThreshold', DEFAULT_CONFIDENCE_THRESHOLD);
  return typeof val === 'number' && val >= 0 && val <= 1 ? val : DEFAULT_CONFIDENCE_THRESHOLD;
}

/** Whether inbox captures should auto-classify. Default true (legacy behavior). */
function isAutoTriageEnabled() {
  return getPref('aiAutoTriage', true) !== false;
}

/** The user's fallback capture template key (a BUILTIN_TEMPLATES id). */
function getDefaultCaptureTemplateId() {
  return getPref('defaultCaptureTemplate', 'general');
}

/**
 * Resolve the promptSuffix for a template id from BUILTIN_TEMPLATES.
 * Unknown id → '' (the 'general' template's suffix is already '').
 */
function templateSuffixFor(templateId) {
  const t = BUILTIN_TEMPLATES.find(x => x.id === templateId);
  return t ? t.promptSuffix : '';
}

// ---------- Key Management ----------

const ENV_KEY = import.meta.env.VITE_GEMINI_API_KEY;
const SESSION_KEY = 'alphafocus_gemini_key_session';

/**
 * Retrieve the Gemini API key. Priority:
 *   1. Environment variable (build-time, most secure)
 *   2. Storage adapter (persistent, synced to Firestore)
 *   3. sessionStorage (ephemeral — set from wizard prompt, cleared on tab close)
 */
export function getApiKey() {
  return ENV_KEY || storage.get('gemini_api_key', '') || sessionStorage.getItem(SESSION_KEY) || '';
}

/**
 * Store the API key. Called from Settings page (persistent) or wizard prompt (session).
 * @param {string} key
 * @param {boolean} persistent — true = storage adapter (Settings), false = sessionStorage (prompt)
 */
export function setApiKey(key, persistent = true) {
  if (key) {
    if (persistent) {
      storage.set('gemini_api_key', key);
      sessionStorage.removeItem(SESSION_KEY); // clean up session copy
    } else {
      sessionStorage.setItem(SESSION_KEY, key);
    }
  } else {
    storage.set('gemini_api_key', '');
    sessionStorage.removeItem(SESSION_KEY);
  }
}

/** Returns true when a usable key is available. */
export function hasApiKey() {
  return Boolean(getApiKey());
}

// ---------- Prompt Templates ----------

export const BUILTIN_TEMPLATES = [
  {
    id: 'general',
    name: 'General',
    emoji: '🧠',
    description: 'Full-spectrum — extract tasks, projects, areas, identities, flows.',
    promptSuffix: '',
    builtin: true,
  },
  {
    id: 'identities',
    name: 'Identities',
    emoji: '🎭',
    description: 'Create and manage work personas with custom timer settings.',
    promptSuffix: 'MODE FOCUS: IDENTITIES. Prioritize creating/updating identities. Infer work personas from the tasks and projects the user mentions. Suggest timer settings that match the work type (deep work = 60-90min focus, meetings = 25min focus). You can still create tasks/projects as supporting actions. If the user describes a type of work without mentioning an identity, proactively suggest creating one.',
    builtin: true,
  },
  {
    id: 'flows',
    name: 'Flows',
    emoji: '📋',
    description: 'Build focus session plans with time-allocated steps.',
    promptSuffix: 'MODE FOCUS: FLOWS. Prioritize building focus session flows. When the user lists tasks they want to work on, create a flow with time-allocated steps. Suggest which identity fits the work. Link existing tasks to flow steps via taskNames. You can still create tasks as supporting actions if they don\'t exist yet.',
    builtin: true,
  },
  {
    id: 'projects',
    name: 'Projects',
    emoji: '📁',
    description: 'Create tasks scoped to a project, suggest organization.',
    promptSuffix: 'MODE FOCUS: PROJECTS. Prioritize creating tasks within a project context. If the user is talking about work that belongs to a specific project, auto-link tasks to it. Suggest subtask decomposition for complex items. When tasks don\'t fit an existing project, suggest creating one.',
    builtin: true,
  },
  {
    id: 'session-planning',
    name: 'Session',
    emoji: '⏱️',
    description: 'Plan your work session — tasks, flow, identity, all at once.',
    promptSuffix: 'MODE FOCUS: SESSION PLANNING. The user wants to plan a work session. Orchestrate everything: check if tasks exist (create if not), group into a project if they share a theme, create a flow with time estimates per task, suggest or activate an identity, set scheduledDate to today. Produce a complete session plan in one pass.',
    builtin: true,
  },
  {
    id: 'weekly-planning',
    name: 'Weekly',
    emoji: '📅',
    description: 'Focus on scheduling with aggressive date inference.',
    promptSuffix: 'MODE FOCUS: WEEKLY PLANNING. Focus on scheduling and planning for the week ahead. Infer due dates aggressively from context ("this week" = current week, "next week" = following week). Create tasks with specific due dates and scheduledDates whenever possible. Suggest reorganization if areas/projects seem messy.',
    builtin: true,
  },
  {
    id: 'meeting-notes',
    name: 'Meeting',
    emoji: '🤝',
    description: 'Extract concrete action items from meetings.',
    promptSuffix: 'MODE FOCUS: MEETING ACTION ITEMS. Extract only concrete action items with clear deliverables. Ignore discussion topics, opinions, and context. Each action should be a task. Set priority based on urgency cues. Group by project if applicable.',
    builtin: true,
  },
];

/** Get all built-in templates. */
export function getBuiltinTemplates() {
  return BUILTIN_TEMPLATES;
}

// ---------- Data Context Builder ----------

/**
 * Build a textual snapshot of the user's existing entities.
 * This gets injected into the Gemini prompt so the AI can reference/match them.
 * Includes relationship data (task→project, task→area, project→area) so the
 * AI can handle bulk operations like "all tasks in Project X".
 */
export function buildDataContext(stores) {
  const { tasks, projects, areas, resources, flows, timerSettings, identities, activeIdentity, quests } = stores;

  // Build lookup maps for resolving IDs → names
  const projectMap = {};
  (projects || []).forEach(p => { projectMap[p.id] = p.name; });
  const areaMap = {};
  (areas || []).forEach(a => { areaMap[a.id] = a.name; });

  const lines = [];

  // Tasks (only active)
  const activeTasks = (tasks || []).filter(t => t.status !== 'done');
  if (activeTasks.length > 0) {
    lines.push(`EXISTING TASKS (${activeTasks.length} active):`);
    activeTasks.forEach(t => {
      const parts = [`"${t.title}"`];
      // Include task ID so the AI can reference exact entities
      parts.push(`id: ${t.id}`);
      if (t.priority) parts.push(`priority: ${t.priority}`);
      if (t.status) parts.push(`status: ${t.status}`);
      if (t.dueDate) parts.push(`due: ${t.dueDate}`);
      // Relationship data — critical for bulk operations
      const projName = t.projectId ? projectMap[t.projectId] : null;
      const areaName = t.areaId ? areaMap[t.areaId] : null;
      if (projName) parts.push(`project: "${projName}"`);
      if (areaName) parts.push(`area: "${areaName}"`);
      if (t.subtasks?.length > 0) parts.push(`${t.subtasks.length} subtasks`);
      // Include creation date for temporal context
      if (t.createdAt) parts.push(`created: ${t.createdAt.split('T')[0]}`);
      lines.push(`  - ${parts.join(', ')}`);
    });
  }

  // Projects
  if (projects?.length > 0) {
    lines.push(`\nEXISTING PROJECTS (${projects.length}):`);
    projects.forEach(p => {
      const parts = [`"${p.name}"`];
      parts.push(`status: ${p.status || 'active'}`);
      if (p.dueDate) parts.push(`due: ${p.dueDate}`);
      const areaName = p.areaId ? areaMap[p.areaId] : null;
      if (areaName) parts.push(`area: "${areaName}"`);
      // Count tasks in this project
      const taskCount = (tasks || []).filter(t => t.projectId === p.id && t.status !== 'done').length;
      if (taskCount > 0) parts.push(`${taskCount} active tasks`);
      lines.push(`  - ${parts.join(', ')}`);
    });
  }

  // Areas
  if (areas?.length > 0) {
    lines.push(`\nEXISTING AREAS (${areas.length}):`);
    areas.forEach(a => lines.push(`  - "${a.name}"`));
  }

  // Resources (active only)
  const activeResources = (resources || []).filter(r => r.status === 'active');
  if (activeResources.length > 0) {
    lines.push(`\nEXISTING RESOURCES (${activeResources.length} active):`);
    activeResources.forEach(r => {
      lines.push(`  - "${r.title}" (${r.type || 'note'})`);
    });
  }

  // Flows
  if (flows?.length > 0) {
    lines.push(`\nEXISTING FLOWS (${flows.length}):`);
    flows.forEach(b => {
      const stepCount = b.steps?.length || 0;
      lines.push(`  - "${b.name}" (${stepCount} steps)`);
    });
  }

  // Timer settings
  if (timerSettings) {
    lines.push(`\nCURRENT TIMER SETTINGS:`);
    lines.push(`  - Focus: ${Math.round((timerSettings.focusDuration || 3000) / 60)} min`);
    lines.push(`  - Rest: ${Math.round((timerSettings.restDuration || 600) / 60)} min`);
    if (timerSettings.reflectDuration) {
      lines.push(`  - Reflect: ${Math.round(timerSettings.reflectDuration / 60)} min`);
    }
    if (timerSettings.prepDuration) {
      lines.push(`  - Prep: ${Math.round(timerSettings.prepDuration / 60)} min`);
    }
  }

  // Identities
  if (identities?.length > 0) {
    lines.push(`\nEXISTING IDENTITIES (${identities.length}):`);
    identities.forEach(i => {
      const isActive = activeIdentity?.id === i.id ? ' [ACTIVE]' : '';
      const focus = i.settings?.durations?.focus ? Math.round(i.settings.durations.focus / 60) : '?';
      const rest = i.settings?.durations?.rest ? Math.round(i.settings.durations.rest / 60) : '?';
      lines.push(`  - ${i.emoji || '🎭'} "${i.name}"${isActive} (focus: ${focus}min, rest: ${rest}min)`);
    });
  }

  // Quests (GPS: Goal / Plan / System) — the strategic prioritization layer.
  // Main quests outrank side quests; the AI uses `type` to prioritize.
  // Rendered only when the caller passes quests (it gates by the 'quests' module).
  // Only LIVE quests are a current priority signal — exclude finished (completed)
  // and dropped (abandoned) ones, mirroring the task (status !== 'done') filter.
  const activeQuests = (quests || []).filter(q => q.status !== 'abandoned' && q.status !== 'completed');
  if (activeQuests.length > 0) {
    // Main quests first so the priority signal reads top-down.
    const ordered = [...activeQuests].sort((a, b) => {
      if (a.type === b.type) return 0;
      return a.type === 'main' ? -1 : 1;
    });
    lines.push(`\nEXISTING QUESTS (${activeQuests.length} active) — main quests take priority over side quests:`);
    ordered.forEach(q => {
      const parts = [`"${q.name}"`];
      if (q.type) parts.push(`type: ${q.type}`);
      if (q.domain) parts.push(`domain: ${q.domain}`);
      if (typeof q.progress === 'number') parts.push(`progress: ${q.progress}%`);
      lines.push(`  - ${parts.join(', ')}`);
      const truncate = (s, n = 120) => {
        const t = String(s || '').trim().replace(/\s+/g, ' ');
        return t.length > n ? `${t.slice(0, n)}…` : t;
      };
      if (q.goal) lines.push(`      goal: ${truncate(q.goal)}`);
      if (q.plan) lines.push(`      plan: ${truncate(q.plan)}`);
      if (q.system) lines.push(`      system: ${truncate(q.system)}`);
      // Resolve linked project names via the projectMap already built.
      const linkedNames = (q.projectIds || [])
        .map(id => projectMap[id])
        .filter(Boolean);
      if (linkedNames.length > 0) {
        lines.push(`      projects: ${linkedNames.map(n => `"${n}"`).join(', ')}`);
      }
    });
  }

  return lines.join('\n');
}

// ---------- System Prompt ----------



// ---------- Voice Dump Processing ----------

/**
 * Process a voice dump audio blob through Gemini multimodal.
 * @param {Blob} audioBlob — recorded audio (webm/ogg)
 * @param {string} dataContext — textual context from buildDataContext()
 * @param {string} [templatePrompt] — optional template-specific prompt suffix
 * @returns {{ transcript: string, actions: Array, summary: string }}
 */
/**
 * Process a voice dump — dispatches to proxy or BYOK mode.
 * @param {Blob} audioBlob — recorded audio (webm/ogg)
 * @param {string} dataContext — textual context from buildDataContext()
 * @param {string} [templatePrompt] — optional template-specific prompt suffix
 * @param {string} [mode] — 'proxy' | 'byok' (auto-detected if not provided)
 * @returns {{ transcript: string, actions: Array, summary: string, creditsUsed?: number, creditsRemaining?: number }}
 */
export async function processVoiceDump(audioBlob, dataContext, templatePrompt = '', mode = null) {
  // Convert blob to base64 (needed for both modes)
  const arrayBuffer = await audioBlob.arrayBuffer();
  const base64Audio = btoa(
    new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
  );
  const mimeType = audioBlob.type || 'audio/webm';

  // When the caller doesn't specify a template, fall back to the user's
  // defaultCaptureTemplate pref (default 'general', whose suffix is '' — so the
  // legacy no-template behavior is preserved exactly for default-config users).
  const effectiveTemplatePrompt = templatePrompt || templateSuffixFor(getDefaultCaptureTemplateId());
  const { modelTier } = getAIRoutingPrefs();

  // ─── PROXY MODE: Cloud Functions handle everything ───
  if (mode === 'proxy') {
    try {
      return await proxyVoiceDump({
        audioBase64: base64Audio,
        mimeType,
        dataContext,
        templatePrompt: effectiveTemplatePrompt,
        systemPrompt: SYSTEM_PROMPT,
        ...(modelTier !== undefined ? { modelTier } : {}),
      });
    } catch (error) {
      // Map Firebase callable errors to our error codes
      if (error.code === 'functions/resource-exhausted') {
        const details = error.details;
        if (details?.code === 'INSUFFICIENT_CREDITS') {
          throw new Error('INSUFFICIENT_CREDITS');
        }
        throw new Error('QUOTA_EXCEEDED');
      }
      throw new Error(`API_ERROR: ${error.message}`);
    }
  }

  // ─── BYOK MODE: Direct client → Gemini call ───
  const apiKey = getApiKey();
  if (!apiKey) {
    throw new Error('NO_API_KEY');
  }

  const ai = new GoogleGenAI({ apiKey });

  try {
    const response = await ai.models.generateContent({
      model: getByokModel(),
      contents: [
        {
          role: 'user',
          parts: [
            {
              inlineData: {
                mimeType,
                data: base64Audio,
              },
            },
            {
              text: `CURRENT DATE/TIME: ${localToday()} (${new Date().toLocaleDateString('en-US', { weekday: 'long' })}), ${new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}\n\nHere is the user's current data context:\n\n${dataContext}\n\n${effectiveTemplatePrompt ? `${effectiveTemplatePrompt}\n\n` : ''}Analyze the audio above and extract all actionable items. Respond with JSON only.`,
            },
          ],
        },
      ],
      config: {
        systemInstruction: SYSTEM_PROMPT,
        responseMimeType: 'application/json',
      },
    });

    const text = response.text;
    let parsed;

    try {
      parsed = JSON.parse(text);
    } catch {
      const jsonMatch = text.match(/```(?:json)?\s*([\s\S]*?)```/);
      if (jsonMatch) {
        parsed = JSON.parse(jsonMatch[1].trim());
      } else {
        throw new Error('MALFORMED_RESPONSE');
      }
    }

    if (!parsed.actions || !Array.isArray(parsed.actions)) {
      parsed.actions = [];
    }
    if (!parsed.transcript) parsed.transcript = '';
    if (!parsed.summary) parsed.summary = `${parsed.actions.length} action(s) extracted.`;

    parsed.actions = parsed.actions.map((action, index) => ({
      ...action,
      id: `action-${index}`,
      selected: true,
    }));

    return parsed;
  } catch (error) {
    if (error.message === 'NO_API_KEY' || error.message === 'MALFORMED_RESPONSE') {
      throw error;
    }
    if (error.status === 429) {
      throw new Error('QUOTA_EXCEEDED');
    }
    throw new Error(`API_ERROR: ${error.message}`);
  }
}

// ========== AI COACH ==========



/**
 * Send a text message to the AI Coach and get a coaching response.
 * @param {Object} params
 * @param {string} params.message — The user's message
 * @param {Array} params.conversationHistory — Previous messages [{ role, content }]
 * @param {string} params.dataContext — Textual snapshot from buildDataContext()
 * @param {string} [params.pageContext] — Optional page-level context
 * @returns {{ content: string }}
 */
/**
 * Send a text message to the AI Coach — dispatches to proxy or BYOK mode.
 * @param {Object} params
 * @param {string} params.message
 * @param {Array} params.conversationHistory
 * @param {string} params.dataContext
 * @param {string} [params.pageContext]
 * @param {string} [params.mode] — 'proxy' | 'byok' (auto-detected if not provided)
 * @returns {{ content: string, creditsUsed?: number, creditsRemaining?: number }}
 */
export async function chatWithCoach({ message, conversationHistory = [], dataContext, pageContext = '', mode = null }) {
  // Merge pageContext into dataContext for both modes
  const fullContext = pageContext
    ? `${dataContext}\n\nUSER IS CURRENTLY ON: ${pageContext}`
    : dataContext;

  // ─── PROXY MODE: Cloud Functions handle everything ───
  if (mode === 'proxy') {
    try {
      // Forward the user's tone/model KEYS (S9: keys only, never raw prompts).
      // getAIRoutingPrefs omits absent keys → old behavior when unset.
      return await proxyChatWithCoach({
        message: message.trim(),
        conversationHistory,
        dataContext: fullContext,
        ...getAIRoutingPrefs(),
      });
    } catch (error) {
      if (error.code === 'functions/resource-exhausted') {
        const details = error.details;
        if (details?.code === 'INSUFFICIENT_CREDITS') {
          throw new Error('INSUFFICIENT_CREDITS');
        }
        throw new Error('QUOTA_EXCEEDED');
      }
      throw new Error(`API_ERROR: ${error.message}`);
    }
  }

  // ─── BYOK MODE: Direct client → Gemini call ───
  const apiKey = getApiKey();
  if (!apiKey) {
    throw new Error('NO_API_KEY');
  }

  const ai = new GoogleGenAI({ apiKey });

  const contents = [];
  const contextPreamble = `Here is the user's current data:\n\n${fullContext}`;

  if (conversationHistory.length > 0) {
    const firstHistory = conversationHistory[0];
    if (firstHistory.role === 'user') {
      contents.push({
        role: 'user',
        parts: [{ text: `${contextPreamble}\n\n${firstHistory.content}` }],
      });
    } else {
      contents.push({
        role: 'user',
        parts: [{ text: contextPreamble }],
      });
      contents.push({
        role: 'model',
        parts: [{ text: firstHistory.content }],
      });
    }

    for (let i = 1; i < conversationHistory.length; i++) {
      const msg = conversationHistory[i];
      contents.push({
        role: msg.role === 'assistant' ? 'model' : 'user',
        parts: [{ text: msg.content }],
      });
    }

    contents.push({
      role: 'user',
      parts: [{ text: message }],
    });
  } else {
    contents.push({
      role: 'user',
      parts: [{ text: `${contextPreamble}\n\n${message}` }],
    });
  }

  try {
    const response = await ai.models.generateContent({
      model: getByokModel(),
      contents,
      config: {
        systemInstruction: COACH_SYSTEM_PROMPT,
      },
    });

    const text = response.text || '';
    return { content: text };
  } catch (error) {
    if (error.status === 429) {
      throw new Error('QUOTA_EXCEEDED');
    }
    throw new Error(`API_ERROR: ${error.message}`);
  }
}

// ========== INBOX CLASSIFICATION (Quick Capture AI) ==========

/**
 * Classify an inbox task into a project or area using Gemini Flash.
 * Non-blocking, fire-and-forget from the caller's perspective.
 *
 * @param {string} taskTitle
 * @param {Array} projects — [{ id, name, emoji, status }]
 * @param {Array} areas — [{ id, name, emoji }]
 * @returns {{ projectId?: string, areaId?: string, confidence: number, reason: string } | null}
 */
export async function classifyInboxItem(taskTitle, projects, areas) {
  if (!taskTitle?.trim()) return null;

  // Honor the aiAutoTriage pref: when disabled, skip auto-classification
  // entirely so captures land un-triaged (caller sees null, same as no match).
  if (!isAutoTriageEnabled()) return null;

  const confidenceThreshold = getConfidenceThreshold();

  const projectList = (projects || [])
    .filter(p => p.status === 'active')
    .map(p => `  - id: "${p.id}", name: "${p.name}"`).join('\n');

  const areaList = (areas || [])
    .map(a => `  - id: "${a.id}", name: "${a.name}"`).join('\n');

  const prompt = `You are a task categorization assistant. Given a task title and a list of existing projects and areas, suggest the best match.

TASK TITLE: "${taskTitle}"

PROJECTS:
${projectList || '(none)'}

AREAS:
${areaList || '(none)'}

RULES:
- Only suggest a match if you are reasonably confident (>${confidenceThreshold}).
- Return EXACTLY one of: projectId, areaId, or null for both if no good match.
- Confidence is a float between 0 and 1.
- Provide a short reason (max 10 words).

Respond with JSON only: { "projectId": string|null, "areaId": string|null, "confidence": number, "reason": string }`;

  try {
    const apiKey = getApiKey();
    if (!apiKey) return null;

    const ai = new GoogleGenAI({ apiKey });
    const response = await ai.models.generateContent({
      model: getByokModel(),
      contents: [{ role: 'user', parts: [{ text: prompt }] }],
      config: { responseMimeType: 'application/json' },
    });

    const text = response.text;
    const parsed = JSON.parse(text);

    // Validate returned IDs actually exist
    if (parsed.projectId && !projects.find(p => p.id === parsed.projectId)) {
      parsed.projectId = null;
    }
    if (parsed.areaId && !areas.find(a => a.id === parsed.areaId)) {
      parsed.areaId = null;
    }

    if (!parsed.projectId && !parsed.areaId) return null;
    // Pref-driven confidence gate (default 0.6 preserves legacy behavior).
    if ((parsed.confidence || 0) < confidenceThreshold) return null;

    return parsed;
  } catch {
    // Classification failure is non-critical — silently return null
    return null;
  }
}

/**
 * Bulk triage inbox items. Suggests if they are Tasks, Projects, or Notes.
 * 
 * @param {Array} items - Array of { id, content }
 * @param {Array} projects - Array of existing projects
 * @param {Array} areas - Array of existing areas
 * @returns {Object} Mapping of id -> { type: 'task'|'project'|'note', title: string, projectId: string|null, areaId: string|null, reason: string }
 */
export async function triageInboxItems(items, projects, areas) {
  if (!items || items.length === 0) return {};

  const projectList = (projects || [])
    .filter(p => p.status === 'active')
    .map(p => `  - id: "${p.id}", name: "${p.name}"`).join('\n');

  const areaList = (areas || [])
    .map(a => `  - id: "${a.id}", name: "${a.name}"`).join('\n');

  const itemsList = items.map(i => `  - id: "${i.id}", content: "${(typeof i.content === 'object' ? getPlainText(i.content) : String(i.content || '')).slice(0, 200).replace(/\n/g, ' ')}"`).join('\n');

  const prompt = `You are a Smart Inbox Triage AI. 
Analyze the following raw inbox items and determine what they should become:
1. "task": Actionable items. You can optionally route them to an existing projectId or areaId if it strongly matches.
2. "project": A large endeavor requiring multiple steps (e.g. "Plan vacation", "Build new feature").
3. "note": Reference material, ideas, brain dumps, or resources with no clear immediate action.

Extract a clean "title" from the content for tasks and projects.

EXISTING PROJECTS:
${projectList || '(none)'}

EXISTING AREAS:
${areaList || '(none)'}

ITEMS TO TRIAGE:
${itemsList}

Return a JSON array of objects with this schema:
[{ "id": string, "type": "task"|"project"|"note", "title": string, "projectId": string|null, "areaId": string|null, "reason": string }]
Only use IDs that exist in the provided lists.`;

  try {
    const apiKey = getApiKey();
    let text = '';

    if (apiKey) {
      const ai = new GoogleGenAI({ apiKey });
      const response = await ai.models.generateContent({
        model: getByokModel(),
        contents: [{ role: 'user', parts: [{ text: prompt }] }],
        config: { responseMimeType: 'application/json' },
      });
      text = response.text;
    } else {
      // Fallback to proxy
      const response = await proxyChatWithCoach({
        message: prompt,
        conversationHistory: [],
        dataContext: '',
        promptMode: 'triage',
        ...getAIRoutingPrefs(),
      });
      text = response.content;
      // Strip markdown code blocks if the proxy model returns them
      text = text.replace(/```json/g, '').replace(/```/g, '').trim();
    }

    const parsedArray = JSON.parse(text);
    const result = {};

    for (const item of parsedArray) {
      if (!item.id || !item.type) continue;
      
      if (item.projectId && !projects.find(p => p.id === item.projectId)) item.projectId = null;
      if (item.areaId && !areas.find(a => a.id === item.areaId)) item.areaId = null;
      
      result[item.id] = item;
    }

    return result;
  } catch (error) {
    console.error("Triage failed:", error);
    return {};
  }
}

/**
 * Analyze a raw text capture using Gemini and extract structured actions.
 * @param {string} text - Raw brain dump text
 * @param {string} dataContext - User app context
 * @param {string} [mode] - 'proxy' | 'byok'
 * @returns {Promise<Object>} Object containing summary, transcript (original text), and structured actions
 */
export async function extractActionsFromText(text, dataContext, mode = null) {
  if (!text || !text.trim()) return { transcript: '', actions: [], summary: '' };

  // Fallback capture template (default 'general', suffix '' → legacy prompt).
  const templateSuffix = templateSuffixFor(getDefaultCaptureTemplateId());
  const prompt = `CURRENT DATE/TIME: ${localToday()} (${new Date().toLocaleDateString('en-US', { weekday: 'long' })}), ${new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}\n\nHere is the user's current data context:\n\n${dataContext}\n\n${templateSuffix ? `${templateSuffix}\n\n` : ''}Analyze the text capture below and extract all actionable items. Respond with JSON only.\n\nTEXT CAPTURE:\n${text}`;

  try {
    const apiKey = getApiKey();
    const finalMode = mode || (apiKey ? 'byok' : 'proxy');
    let responseText = '';

    if (finalMode === 'byok' && apiKey) {
      const ai = new GoogleGenAI({ apiKey });
      const response = await ai.models.generateContent({
        model: getByokModel(),
        contents: [{ role: 'user', parts: [{ text: prompt }] }],
        config: {
          systemInstruction: SYSTEM_PROMPT,
          responseMimeType: 'application/json',
        },
      });
      responseText = response.text;
    } else {
      // Use AI Coach proxy endpoint for text triage
      const response = await proxyChatWithCoach({
        message: prompt,
        conversationHistory: [],
        dataContext: '',
        promptMode: 'extract',
        ...getAIRoutingPrefs(),
      });
      responseText = response.content;
      responseText = responseText.replace(/```json/g, '').replace(/```/g, '').trim();
    }

    let parsed = JSON.parse(responseText);
    if (!parsed.actions || !Array.isArray(parsed.actions)) {
      parsed.actions = [];
    }
    parsed.transcript = text;
    parsed.actions = parsed.actions.map((action, index) => ({
      ...action,
      id: `action-${index}`,
      selected: true,
    }));

    return parsed;
  } catch (error) {
    console.error("Text extraction failed:", error);
    return { transcript: text, actions: [], summary: 'Failed to run extraction.' };
  }
}

